import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Compass, Check, Plane, User, Award } from "lucide-react";
import { apiJson } from "../lib/queryClient";
import { useAuth } from "../lib/auth";
import { setSetupGuideStatus, persistServerSetupGuideStatus } from "../lib/setupGuide";
import {
  ALL_PRESETS,
  searchPresets,
  getDefaultCategoryForPreset,
  type DronePreset,
} from "@client/lib/dronePresets";

type Step = "choice" | "drones" | "pilot" | "licenses" | "done";

const DEFAULT_LIMITS = {
  maxWind: 10,
  maxGusts: 12,
  maxRain: 0.5,
  minTemp: 0,
  maxTemp: 40,
  minCloudCeiling: 50,
  minVisibility: 5000,
};

const DEFAULT_COMPLIANCE = {
  operatorRegistered: false,
  eIdLabeledOnDrone: false,
  insuranceActive: false,
  competencyA1A3: false,
  certificateA2: false,
  remoteIdConfigured: false,
};

type PilotProfile = {
  id?: string;
  name: string;
  pilotId: string;
  operatorId: string;
  licenseA1A3: boolean;
  licenseA2: boolean;
  licenseSTS: boolean;
  insuranceActive: boolean;
  [key: string]: unknown;
};

export default function SetupGuide() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [step, setStep] = useState<Step>("choice");

  function finish(result: "completed" | "skipped") {
    // Lokal (Best-Effort) UND konto-gebunden auf dem Server ablegen, damit der
    // Assistent geräteunabhängig nicht erneut angeboten wird.
    setSetupGuideStatus(result, userId);
    void persistServerSetupGuideStatus(result);
    navigate("/", { replace: true });
  }

  return (
    <div className="auth-shell">
      <div className="mx-auto max-w-xl px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-[var(--color-app-text)]">Einrichtung</h1>
          <button
            type="button"
            className="text-sm text-[var(--color-app-text-secondary)] hover:underline"
            onClick={() => finish(step === "done" ? "completed" : "skipped")}
            data-testid="button-setup-close"
          >
            Schließen
          </button>
        </div>

        {step === "choice" ? (
          <ChoiceStep onStart={() => setStep("drones")} onSkip={() => finish("skipped")} />
        ) : null}
        {step === "drones" ? <DronesStep onNext={() => setStep("pilot")} /> : null}
        {step === "pilot" ? <PilotStep onNext={() => setStep("licenses")} /> : null}
        {step === "licenses" ? <LicensesStep onNext={() => setStep("done")} /> : null}
        {step === "done" ? <DoneStep onClose={() => finish("completed")} /> : null}
      </div>
    </div>
  );
}

function StepHeader({ index, title, subtitle }: { index: number; title: string; subtitle: string }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-bold uppercase tracking-wide text-[var(--color-brand-500)]">
        Schritt {index} von 3
      </div>
      <h2 className="text-2xl font-bold text-[var(--color-app-text)] mt-1">{title}</h2>
      <p className="text-sm text-[var(--color-app-text-secondary)] mt-1 leading-relaxed">{subtitle}</p>
    </div>
  );
}

function ChoiceStep({ onStart, onSkip }: { onStart: () => void; onSkip: () => void }) {
  return (
    <div className="card text-center py-10 px-6 flex flex-col items-center gap-4">
      <div className="size-20 rounded-full bg-[rgba(245,158,11,0.15)] grid place-items-center">
        <Compass size={36} className="text-[var(--color-brand-500)]" />
      </div>
      <h2 className="text-2xl font-bold text-[var(--color-app-text)]">Willkommen an Bord</h2>
      <p className="text-sm text-[var(--color-app-text-secondary)] leading-relaxed max-w-sm">
        Richte deine Drohnen, dein Pilotenprofil und deine Lizenzen in wenigen Schritten ein.
        Du kannst jeden Schritt überspringen und später in den Einstellungen fortsetzen.
      </p>
      <div className="w-full max-w-xs space-y-2 mt-2">
        <button className="btn-primary w-full" onClick={onStart} data-testid="button-setup-start">
          Geführtes Setup starten
        </button>
        <button className="btn-secondary w-full" onClick={onSkip} data-testid="button-setup-skip-all">
          Später / Überspringen
        </button>
      </div>
    </div>
  );
}

function DronesStep({ onNext }: { onNext: () => void }) {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [added, setAdded] = useState<string[]>([]);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [mName, setMName] = useState("");
  const [mManufacturer, setMManufacturer] = useState("");
  const [mModel, setMModel] = useState("");
  const [mClass, setMClass] = useState("C0");
  const [mMtom, setMMtom] = useState("249");

  const results = useMemo<DronePreset[]>(() => {
    const q = query.trim();
    return (q ? searchPresets(q) : ALL_PRESETS).slice(0, 20);
  }, [query]);

  async function addPreset(p: DronePreset) {
    const label = `${p.manufacturer} ${p.model}`.trim();
    setSavingName(label);
    try {
      await apiJson("POST", "/api/drones", {
        name: label,
        manufacturer: p.manufacturer,
        model: p.model,
        classMark: p.classMark,
        mtomGrams: p.mtomGrams,
        openCategory: getDefaultCategoryForPreset(p),
        droneType: p.droneType,
        hasCameraOrSensor: true,
        compliance: DEFAULT_COMPLIANCE,
        limits: DEFAULT_LIMITS,
      });
      setAdded((prev) => [...prev, label]);
      qc.invalidateQueries({ queryKey: ["/api/drones"] });
    } finally {
      setSavingName(null);
    }
  }

  async function addManual(e: FormEvent) {
    e.preventDefault();
    const name = mName.trim() || `${mManufacturer} ${mModel}`.trim();
    if (!name) return;
    setSavingName(name);
    try {
      await apiJson("POST", "/api/drones", {
        name,
        manufacturer: mManufacturer.trim() || null,
        model: mModel.trim() || null,
        classMark: mClass.trim() || "C0",
        mtomGrams: parseInt(mMtom, 10) || 0,
        openCategory: "A3",
        droneType: "standard",
        hasCameraOrSensor: true,
        compliance: DEFAULT_COMPLIANCE,
        limits: DEFAULT_LIMITS,
      });
      setAdded((prev) => [...prev, name]);
      qc.invalidateQueries({ queryKey: ["/api/drones"] });
      setManualOpen(false);
      setMName("");
      setMManufacturer("");
      setMModel("");
      setMClass("C0");
      setMMtom("249");
    } finally {
      setSavingName(null);
    }
  }

  return (
    <div>
      <StepHeader
        index={1}
        title="Drohnen"
        subtitle="Wähle deine Drohnen aus der Liste oder lege sie manuell an. Du kannst mehrere hinzufügen."
      />

      {added.length > 0 ? (
        <div className="flex items-start gap-2 rounded-xl bg-[var(--color-go-bg)] text-[var(--color-go)] px-4 py-3 text-sm mb-4">
          <Check size={16} className="mt-0.5 shrink-0" />
          <span>
            {added.length} {added.length === 1 ? "Drohne" : "Drohnen"} hinzugefügt: {added.join(", ")}
          </span>
        </div>
      ) : null}

      <input
        className="input mb-3"
        placeholder="Drohne suchen (z. B. Mavic)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid="input-setup-drone-search"
      />

      <div className="space-y-2 max-h-72 overflow-y-auto">
        {results.map((p) => {
          const label = `${p.manufacturer} ${p.model}`.trim();
          const isSaving = savingName === label;
          return (
            <button
              key={`${p.manufacturer}-${p.model}-${p.year}`}
              type="button"
              onClick={() => addPreset(p)}
              disabled={isSaving}
              className="w-full flex items-center gap-3 rounded-xl border border-[var(--color-app-border)] bg-[var(--color-app-bg-elevated)] px-3 py-2.5 hover:bg-white/10 text-left disabled:opacity-50"
              data-testid={`button-setup-preset-${label}`}
            >
              <Plane size={20} className="text-[var(--color-brand-500)] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--color-app-text)] truncate">{label}</div>
                <div className="text-xs text-[var(--color-app-text-secondary)]">
                  {p.classMark} · {p.mtomGrams} g
                </div>
              </div>
              <span className="text-xs text-[var(--color-brand-500)] font-semibold shrink-0">
                {isSaving ? "…" : "+ Hinzufügen"}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="text-sm text-[var(--color-brand-500)] font-semibold mt-3 hover:underline"
        onClick={() => setManualOpen((v) => !v)}
        data-testid="button-setup-manual-toggle"
      >
        {manualOpen ? "Manuell schließen" : "Manuell hinzufügen"}
      </button>

      {manualOpen ? (
        <form onSubmit={addManual} className="mt-3 space-y-3 rounded-xl border border-[var(--color-app-border)] p-3">
          <input className="input" placeholder="Name" value={mName} onChange={(e) => setMName(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <input className="input" placeholder="Hersteller" value={mManufacturer} onChange={(e) => setMManufacturer(e.target.value)} />
            <input className="input" placeholder="Modell" value={mModel} onChange={(e) => setMModel(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input className="input" placeholder="Klasse (C0)" value={mClass} onChange={(e) => setMClass(e.target.value)} />
            <input className="input" type="number" min={0} placeholder="MTOM (g)" value={mMtom} onChange={(e) => setMMtom(e.target.value)} />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={savingName !== null} data-testid="button-setup-manual-add">
            {savingName !== null ? "Speichert …" : "Drohne hinzufügen"}
          </button>
        </form>
      ) : null}

      <StepFooter
        primaryLabel="Weiter"
        onPrimary={onNext}
        onSkip={onNext}
        primaryTestId="button-setup-drones-next"
        skipTestId="button-setup-drones-skip"
      />
    </div>
  );
}

function PilotStep({ onNext }: { onNext: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PilotProfile | null>({ queryKey: ["/api/profile"] });
  const [name, setName] = useState("");
  const [pilotId, setPilotId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setName(data.name || "");
      setPilotId(data.pilotId || "");
      setOperatorId(data.operatorId || "");
    }
  }, [data]);

  async function saveAndNext() {
    setSaving(true);
    try {
      await apiJson("POST", "/api/profile", {
        ...(data || {}),
        name: name.trim(),
        pilotId: pilotId.trim(),
        operatorId: operatorId.trim(),
      });
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
    } finally {
      setSaving(false);
      onNext();
    }
  }

  return (
    <div>
      <StepHeader
        index={2}
        title="Pilotendaten"
        subtitle="Diese Angaben erscheinen in deinen Flugprotokollen. Alle Felder sind optional."
      />
      {isLoading ? (
        <div className="text-center text-[var(--color-app-text-secondary)] py-8">Lädt …</div>
      ) : (
        <div className="card space-y-3">
          <div>
            <label className="label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Max Mustermann" />
          </div>
          <div>
            <label className="label">eID / Fernpiloten-Nr.</label>
            <input className="input" value={pilotId} onChange={(e) => setPilotId(e.target.value)} placeholder="z. B. FRA-..." />
          </div>
          <div>
            <label className="label">Betreiber-Nr. (Operator-ID)</label>
            <input className="input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} placeholder="z. B. DEUop..." />
          </div>
        </div>
      )}
      <StepFooter
        primaryLabel={saving ? "Speichert …" : "Weiter"}
        onPrimary={saveAndNext}
        onSkip={onNext}
        primaryDisabled={saving}
        primaryTestId="button-setup-pilot-next"
        skipTestId="button-setup-pilot-skip"
      />
    </div>
  );
}

function LicensesStep({ onNext }: { onNext: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PilotProfile | null>({ queryKey: ["/api/profile"] });
  const [a1a3, setA1a3] = useState(false);
  const [a2, setA2] = useState(false);
  const [sts, setSts] = useState(false);
  const [insurance, setInsurance] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setA1a3(!!data.licenseA1A3);
      setA2(!!data.licenseA2);
      setSts(!!data.licenseSTS);
      setInsurance(!!data.insuranceActive);
    }
  }, [data]);

  async function saveAndNext() {
    setSaving(true);
    try {
      await apiJson("POST", "/api/profile", {
        ...(data || {}),
        licenseA1A3: a1a3,
        licenseA2: a2,
        licenseSTS: sts,
        insuranceActive: insurance,
      });
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
    } finally {
      setSaving(false);
      onNext();
    }
  }

  return (
    <div>
      <StepHeader
        index={3}
        title="Lizenzen & Versicherung"
        subtitle="Markiere, welche Nachweise du besitzt. Details kannst du später im Pilotenprofil ergänzen."
      />
      {isLoading ? (
        <div className="text-center text-[var(--color-app-text-secondary)] py-8">Lädt …</div>
      ) : (
        <div className="space-y-2">
          <ToggleRow label="EU-Kompetenznachweis A1/A3" value={a1a3} onToggle={() => setA1a3((v) => !v)} testId="toggle-setup-a1a3" />
          <ToggleRow label="Fernpiloten-Zeugnis A2" value={a2} onToggle={() => setA2((v) => !v)} testId="toggle-setup-a2" />
          <ToggleRow label="STS / Spezielle Kategorie" value={sts} onToggle={() => setSts((v) => !v)} testId="toggle-setup-sts" />
          <ToggleRow label="Haftpflichtversicherung aktiv" value={insurance} onToggle={() => setInsurance((v) => !v)} testId="toggle-setup-insurance" />
        </div>
      )}
      <StepFooter
        primaryLabel={saving ? "Speichert …" : "Abschließen"}
        onPrimary={saveAndNext}
        onSkip={onNext}
        primaryDisabled={saving}
        primaryTestId="button-setup-licenses-next"
        skipTestId="button-setup-licenses-skip"
      />
    </div>
  );
}

function DoneStep({ onClose }: { onClose: () => void }) {
  return (
    <div className="card text-center py-10 px-6 flex flex-col items-center gap-4">
      <div className="size-20 rounded-full bg-[var(--color-go-bg)] grid place-items-center">
        <Check size={36} className="text-[var(--color-go)]" />
      </div>
      <h2 className="text-2xl font-bold text-[var(--color-app-text)]">Alles bereit</h2>
      <p className="text-sm text-[var(--color-app-text-secondary)] leading-relaxed max-w-sm">
        Deine Einrichtung ist abgeschlossen. Du kannst alle Angaben jederzeit in den Einstellungen
        anpassen oder den Assistenten dort erneut starten.
      </p>
      <button className="btn-primary w-full max-w-xs mt-2" onClick={onClose} data-testid="button-setup-done">
        Fertig
      </button>
    </div>
  );
}

function ToggleRow({
  label,
  value,
  onToggle,
  testId,
}: {
  label: string;
  value: boolean;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
        value
          ? "border-[var(--color-brand-500)] bg-[rgba(245,158,11,0.12)]"
          : "border-[var(--color-app-border)] bg-[var(--color-app-bg-elevated)] hover:bg-white/10"
      }`}
      data-testid={testId}
    >
      <span className="flex items-center gap-2 text-sm text-[var(--color-app-text)]">
        <Award size={18} className={value ? "text-[var(--color-brand-500)]" : "text-[var(--color-app-text-tertiary)]"} />
        {label}
      </span>
      <span
        className={`size-5 rounded grid place-items-center border ${
          value ? "bg-[var(--color-brand-500)] border-[var(--color-brand-500)] text-white" : "border-[var(--color-app-border)]"
        }`}
      >
        {value ? <Check size={14} /> : null}
      </span>
    </button>
  );
}

function StepFooter({
  primaryLabel,
  onPrimary,
  onSkip,
  primaryDisabled,
  primaryTestId,
  skipTestId,
}: {
  primaryLabel: string;
  onPrimary: () => void;
  onSkip: () => void;
  primaryDisabled?: boolean;
  primaryTestId: string;
  skipTestId: string;
}) {
  return (
    <div className="mt-6 space-y-2">
      <button className="btn-primary w-full" onClick={onPrimary} disabled={primaryDisabled} data-testid={primaryTestId}>
        {primaryLabel}
      </button>
      <button className="btn-secondary w-full" onClick={onSkip} data-testid={skipTestId}>
        Schritt überspringen
      </button>
    </div>
  );
}
